/* eslint-disable react-refresh/only-export-components */
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { resolveAnalyticsElement } from "@/lib/analytics/analyticsElement";
import type { AnalyticsElementId } from "@/lib/analytics/analyticsElementId";
import { cn } from "@/lib/utils";
import { AdminSkeleton } from "@/shared/design-system/admin/AdminSkeleton";
import { Spinner } from "@/shared/ui/spinner";

/**
 * Sweep button finish on two axes: filled / outline / text, and
 * primary / neutral / danger / success. Filled defaults to primary;
 * supporting variants default to neutral, including a quiet neutral outline.
 *
 * Tones name their hue, foreground and solid base. index.css derives the
 * radial lighting, edges, shadows and state colors from those channels.
 * Filled and outline have an opaque face with stationary upper-left light;
 * press removes the gradient and compresses the shadow. Text stays unboxed
 * in every state, with an underline on hover/press and an offset focus ring.
 *
 * Paint stays in utilities so specialized wrappers can replace their own
 * status colors. Disabled controls mute label and glyph together; loading
 * preserves the resting paint while replacing the leading icon with a spinner.
 */
const surfaceClasses = [
  "shadow-(--btn-shadow)",
  "not-disabled:hover:shadow-(--btn-shadow-hover)",
  "not-disabled:active:translate-y-[0.5px] not-disabled:active:bg-none not-disabled:active:shadow-(--btn-shadow-press)",
  "motion-reduce:not-disabled:active:translate-none",
  "disabled:not-aria-busy:border-(--btn-disabled-edge) disabled:not-aria-busy:bg-(--btn-disabled-bg) disabled:not-aria-busy:bg-none disabled:not-aria-busy:shadow-none",
];

const buttonVariants = cva(
  [
    "inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap",
    "rounded-[7px] border border-transparent text-sm font-medium tracking-[-0.01em]",
    "transition-[background-color,border-color,color,box-shadow] duration-[140ms] motion-reduce:transition-none",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:stroke-[1.75] [&_svg:not([class*='size-'])]:size-4",
    "outline-none focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-(--admin-tone-primary-fg)",
    "disabled:pointer-events-none disabled:not-aria-busy:text-(--btn-disabled-fg)",
    // The glyph follows the muted label. No variant pins an icon colour any
    // more, but a wrapper may (AdminDecisionButton paints a status-toned icon
    // per decision), and a pinned colour would otherwise outlive the greying —
    // a bright red icon beside a grey label on a disabled chip. Belongs to the
    // disabled state rather than to any one cell, which is where it used to
    // sit: it was on the outline-neutral compound, and covered the wrappers
    // only because they happen to resolve to that cell.
    "disabled:not-aria-busy:[&_svg]:text-inherit",
    "aria-invalid:border-(--app-status-danger-foreground)",
  ],
  {
    variants: {
      variant: {
        filled: [
          ...surfaceClasses,
          "border-(--btn-solid-edge) bg-(--btn-solid) bg-(image:--admin-btn-fill-image) text-(--btn-solid-fg) font-semibold",
          "not-disabled:hover:border-(--btn-solid-hover) not-disabled:hover:bg-(--btn-solid-hover)",
          "not-disabled:active:border-(--btn-solid-press) not-disabled:active:bg-(--btn-solid-press)",
        ],
        outline: [
          ...surfaceClasses,
          "border-(--btn-outline-edge) bg-(--btn-surface) bg-(image:--admin-btn-surface-image) text-(--btn-fg)",
          "not-disabled:hover:border-(--btn-outline-edge-hover) not-disabled:hover:bg-(--btn-surface-hover)",
          "not-disabled:active:border-(--btn-outline-edge-hover) not-disabled:active:bg-(--btn-surface-press)",
        ],
        text: [
          "bg-transparent bg-none text-(--btn-fg) shadow-none no-underline decoration-1 underline-offset-4",
          "not-disabled:hover:text-(--btn-text-hover) not-disabled:hover:underline not-disabled:hover:decoration-current/65",
          "not-disabled:active:text-(--btn-text-press) not-disabled:active:underline not-disabled:active:decoration-current",
          "disabled:not-aria-busy:text-(--btn-disabled-text-fg)",
        ],
      },
      tone: {
        primary:
          "[--btn-hue:var(--admin-tone-primary)] [--btn-fg:var(--admin-tone-primary-fg)] [--btn-solid-base:var(--admin-btn-solid-bg)] [--btn-solid-fg:var(--admin-btn-solid-fg)]",
        // The neutral chip also names its own hover/press direction: its solid is
        // already at the far end of the page's lightness range, so the shared
        // one would move it by nothing visible. See --admin-btn-neutral-mix.
        neutral:
          "[--btn-hue:var(--admin-tone-neutral)] [--btn-fg:var(--color-text-primary)] [--btn-solid-base:var(--admin-btn-neutral-bg)] [--btn-solid-fg:var(--admin-btn-neutral-text)] [--btn-solid-mix:var(--admin-btn-neutral-mix)]",
        danger:
          "[--btn-hue:var(--admin-tone-danger)] [--btn-fg:var(--admin-tone-danger-fg)] [--btn-solid-base:var(--admin-tone-danger-fg)] [--btn-solid-fg:var(--admin-btn-solid-fg)]",
        success:
          "[--btn-hue:var(--admin-tone-success)] [--btn-fg:var(--admin-tone-success-fg)] [--btn-solid-base:var(--admin-tone-success-fg)] [--btn-solid-fg:var(--admin-btn-solid-fg)]",
      },
      // Label sizes on the shared step vocabulary (sm · md · lg), and the
      // square icon-only sizes on the same steps behind an `icon-` prefix.
      size: {
        sm: "h-8 gap-1.5 px-3 has-[>svg]:px-2.5",
        md: "h-9 px-4 py-2 has-[>svg]:px-3",
        lg: "h-10 px-6 has-[>svg]:px-4",
        "icon-xs": "size-6",
        "icon-sm": "size-8",
        "icon-md": "size-9",
        "icon-lg": "size-10",
      },
    },
    compoundVariants: [
      // Quiet at rest; the shared text hover/press channels retain the tone.
      { variant: "text", tone: "neutral", class: "text-(--color-text-secondary)" },
    ],
    defaultVariants: {
      variant: "filled",
      size: "md",
    },
  },
);

type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>["variant"]>;
type ButtonTone = NonNullable<VariantProps<typeof buttonVariants>["tone"]>;

/**
 * The tone a variant takes when none is given: a filled button is the surface's
 * main action, so it is `primary`; outline and text are supporting actions, so
 * they are `neutral`. Kept out of cva's `defaultVariants` because a default
 * there cannot depend on another axis.
 */
export const resolveButtonTone = (
  variant: ButtonVariant | null | undefined,
  tone: ButtonTone | null | undefined,
): ButtonTone => tone ?? ((variant ?? "filled") === "filled" ? "primary" : "neutral");

function AdminButton({
  className,
  variant,
  tone,
  size,
  asChild = false,
  loading = false,
  loadingLabel,
  disabled,
  analyticsElementId,
  onClick,
  children,
  type,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    /**
     * Pending state for async actions: disables the button and renders a
     * spinner before the label. Use instead of hand-rolling
     * `{isPending ? <Spinner/> : <Icon/>}` swaps at call sites.
     * Ignored with `asChild` (Slot requires a single child).
     */
    loading?: boolean;
    /**
     * Label shown while `loading`. Pass it whenever `children` contains a
     * leading icon (the spinner replaces it) or the wording changes while
     * pending; defaults to `children`.
     */
    loadingLabel?: React.ReactNode;
    /**
     * Structured element id wiring click analytics: stamps the `data-analytics-*` marker
     * attributes and queues `fe_element_clicked` on click (unless disabled or
     * loading).
     */
    analyticsElementId?: AnalyticsElementId;
  }) {
  const Comp = asChild ? Slot : "button";
  const showLoading = loading && !asChild;
  const isIconSize = typeof size === "string" && size.startsWith("icon");
  const isDisabled = disabled || showLoading;
  const resolvedVariant = variant ?? "filled";
  const resolvedTone = resolveButtonTone(variant, tone);
  const analyticsElement = resolveAnalyticsElement(analyticsElementId);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (analyticsElement && !isDisabled) {
      analyticsElement.emitClick();
    }
    onClick?.(event);
  };

  return (
    <Comp
      data-slot="button"
      // The paint derivation in index.css keys on these; they also give tests
      // something more stable than class strings to assert on.
      data-variant={resolvedVariant}
      data-tone={resolvedTone}
      // The HTML default inside a <form> is type="submit", which made every
      // untyped AdminButton in a form dialog an accidental submit target —
      // default to "button" and let submit buttons opt in explicitly. With
      // asChild the child element owns its own type, so pass through as-is.
      type={asChild ? type : (type ?? "button")}
      className={cn(buttonVariants({ variant: resolvedVariant, tone: resolvedTone, size, className }))}
      disabled={isDisabled}
      aria-busy={showLoading || undefined}
      {...analyticsElement?.attributes}
      onClick={analyticsElement ? handleClick : onClick}
      {...props}
    >
      {showLoading ? (
        <>
          {/* The button's label and aria-busy already convey pending work;
              the glyph must not prepend "Loading" to its accessible name. */}
          <Spinner aria-hidden />
          {/* An icon-only button has no label to keep, and its children are the
              glyph the spinner is replacing — rendering both double-icons it. */}
          {isIconSize ? null : (loadingLabel ?? children)}
        </>
      ) : (
        children
      )}
    </Comp>
  );
}

export { AdminButton, buttonVariants };

export const AdminButtonSkeleton = ({ className }: { className?: string }) => (
  <AdminSkeleton shape="rect" className={cn("h-8 w-28", className)} />
);
